import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, LinearProgress, Paper, Typography, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { getMyLiveResult } from '../../../services/liveQuizService';
import { LiveQuizRatingResult } from '../../../types/liveQuiz';
import { TierName } from '../../../types/ranked';
import TierBadge from '../../ranked/TierBadge';
import RatingDelta from '../../ranked/RatingDelta';
import StreakFlame from '../../ranked/StreakFlame';

/** How often to re-poll while the rating batch is still applying. */
const POLL_INTERVAL_MS = 1200;
/** How long to keep polling before giving up and pointing at the profile page instead. */
const MAX_POLL_MS = 15_000;

/**
 * Every reveal delay lives in one object so the sequence reads as a single
 * timeline rather than a scatter of magic numbers. All values are ms from
 * the moment the result lands.
 */
const BEAT = {
  placement: 300,
  delta: 1000,
  odometerStart: 800,
  odometerDurationMs: 1200,
  tier: 2000,
  tierDurationMs: 800,
  rankUp: 2800,
  rankUpBurstGapMs: 200,
  xp: 3400,
  xpDurationMs: 800
};
const REVEAL_TOTAL_MS = BEAT.xp + BEAT.xpDurationMs;

const TIER_ORDER: TierName[] = ['bronze', 'silver', 'gold', 'platinum', 'diamond'];

/** Higher is better; comparable across tiers and divisions (I highest, III lowest). */
function tierRank(name: TierName, division: number): number {
  return TIER_ORDER.indexOf(name) * 3 + (3 - division);
}

function ordinal(n: number): string {
  const suffix = n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th';
  return `${n}${suffix}`;
}

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

function unratedMessage(
  reason: NonNullable<LiveQuizRatingResult['unratedReason']>,
  fieldSize: number
): string {
  switch (reason) {
    case 'field_too_small':
      return `Only ${fieldSize} mathlete${fieldSize === 1 ? '' : 's'} finished — too few for this to count toward your rating. Your XP and streak still count.`;
    case 'attempt_not_ranked':
      return 'You played this after the live event, so it counts for XP but not rating.';
    case 'unranked_replay':
      return 'You missed the live event and played it afterward — that counts for XP and your streak, but not your rating.';
    case 'room_abandoned':
      return "This quiz was stopped early, so it doesn't count toward your rating.";
    default:
      return '';
  }
}

interface RatingRevealCardProps {
  roomId: number;
}

/**
 * Post-quiz rating reveal. Owns its own fetch so the play page stays thin.
 *
 * Ratings are applied by a batch as the room finishes, so the first response
 * is almost always `{ ready: false }` — this polls behind a skeleton until
 * ready or until a bounded retry budget runs out, at which point it points
 * the student at their profile instead of spinning forever.
 */
const RatingRevealCard: React.FC<RatingRevealCardProps> = ({ roomId }) => {
  const theme = useTheme();
  const prefersReducedMotion = useReducedMotion();

  const [result, setResult] = useState<LiveQuizRatingResult | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  // Milliseconds since `result` landed. Every reveal element is a pure
  // function of this single clock rather than owning its own timer — a
  // second timer spawned reactively (e.g. from a stage flag flipping true)
  // would register too late to be driven by a single `advanceTimersByTime`
  // burst in tests, and would be no more correct at runtime either.
  const [elapsedMs, setElapsedMs] = useState(0);
  const rankUpFiredRef = useRef(false);

  // Poll until the rating batch has landed for this room.
  useEffect(() => {
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let elapsed = 0;

    const stop = () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const poll = async () => {
      try {
        const data = await getMyLiveResult(roomId);
        if (cancelled) return;
        if (data.ready) {
          setResult(data);
          stop();
          return;
        }
      } catch {
        // Transient failure — keep polling until the retry budget runs out.
      }
      elapsed += POLL_INTERVAL_MS;
      if (elapsed >= MAX_POLL_MS) {
        stop();
        if (!cancelled) {
          setTimedOut(true);
        }
      }
    };

    void poll();
    intervalId = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      stop();
    };
  }, [roomId]);

  // Drive the whole reveal timeline off one clock, started the instant the
  // result lands. Every element must rest in its final state even if a
  // student never looks again — reduced motion skips straight there instead
  // of racing the clock to zero.
  useEffect(() => {
    if (!result) {
      return undefined;
    }

    if (prefersReducedMotion) {
      setElapsedMs(REVEAL_TOTAL_MS);
      return undefined;
    }

    setElapsedMs(0);
    rankUpFiredRef.current = false;

    const startedAt = Date.now();
    const intervalId = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      setElapsedMs(elapsed);

      if (
        result.rated &&
        result.tierUp &&
        elapsed >= BEAT.rankUp &&
        !rankUpFiredRef.current
      ) {
        rankUpFiredRef.current = true;
        // Two bursts, same call shape as PodiumFinish's celebration.
        void confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
        setTimeout(() => {
          void confetti({ particleCount: 100, spread: 100, origin: { y: 0.6 } });
        }, BEAT.rankUpBurstGapMs);
      }

      if (elapsed >= REVEAL_TOTAL_MS) {
        clearInterval(intervalId);
      }
    }, 50);

    return () => clearInterval(intervalId);
  }, [result, prefersReducedMotion]);

  const showPlacement = prefersReducedMotion || elapsedMs >= BEAT.placement;
  const showDelta = prefersReducedMotion || elapsedMs >= BEAT.delta;
  const showTier = prefersReducedMotion || elapsedMs >= BEAT.tier;
  const showRankUp = Boolean(result?.tierUp) && (prefersReducedMotion || elapsedMs >= BEAT.rankUp);
  const showXp = prefersReducedMotion || elapsedMs >= BEAT.xp;

  const ratingProgress = prefersReducedMotion
    ? 1
    : clamp01((elapsedMs - BEAT.odometerStart) / BEAT.odometerDurationMs);
  const ratingValue = result
    ? result.ratingBefore + (result.ratingAfter - result.ratingBefore) * ratingProgress
    : 0;

  const tierProgressRatio = prefersReducedMotion
    ? 1
    : clamp01((elapsedMs - BEAT.tier) / BEAT.tierDurationMs);
  const tierProgressValue = result
    ? (result.tierBefore.progress +
        (result.tierAfter.progress - result.tierBefore.progress) * tierProgressRatio) *
      100
    : 0;

  const xpProgress = prefersReducedMotion ? 1 : clamp01((elapsedMs - BEAT.xp) / BEAT.xpDurationMs);
  const xpValue = result ? result.xpAwarded * xpProgress : 0;

  if (!result) {
    if (timedOut) {
      return (
        <Paper
          elevation={0}
          sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}
        >
          <Typography sx={{ mb: 2 }}>Your new rank will be on your profile shortly.</Typography>
          <Button component={RouterLink} to="/student/ranked" sx={{ textTransform: 'none' }}>
            View your ranked profile
          </Button>
        </Paper>
      );
    }

    return (
      <Paper
        elevation={0}
        sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}
      >
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          Working out your new rank…
        </Typography>
        <LinearProgress sx={{ borderRadius: 2 }} />
      </Paper>
    );
  }

  const showTierDown =
    result.rated &&
    !result.tierUp &&
    tierRank(result.tierAfter.name, result.tierAfter.division) <
      tierRank(result.tierBefore.name, result.tierBefore.division);

  return (
    <Paper
      component={motion.div}
      initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: prefersReducedMotion ? 0 : 0.4 }}
      elevation={0}
      sx={{ p: 4, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}
    >
      {result.placement !== null && showPlacement && (
        <Typography variant="h6" fontWeight={700} sx={{ textAlign: 'center', mb: 3 }}>
          {ordinal(result.placement)} of {result.fieldSize}
        </Typography>
      )}

      {result.rated ? (
        <>
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              sx={{
                fontFamily: 'monospace',
                fontVariantNumeric: 'tabular-nums',
                fontWeight: 800,
                fontSize: '2.5rem'
              }}
            >
              {Math.round(ratingValue)}
            </Typography>
            {showDelta && (
              <Box sx={{ mt: 1 }}>
                <RatingDelta delta={result.delta} showUnit size="medium" />
              </Box>
            )}
          </Box>

          {showTier && (
            <Box sx={{ mt: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
                <motion.div
                  animate={
                    showRankUp && !prefersReducedMotion ? { scale: [1, 1.15, 1] } : { scale: 1 }
                  }
                  transition={{ duration: 0.6 }}
                >
                  <TierBadge tier={result.tierAfter.name} label={result.tierAfter.label} size="large" />
                </motion.div>
              </Box>
              <LinearProgress
                variant="determinate"
                value={tierProgressValue}
                sx={{ height: 10, borderRadius: 5 }}
              />

              {showRankUp && (
                <Typography
                  variant="h5"
                  fontWeight={800}
                  sx={{ textAlign: 'center', mt: 2, color: theme.palette.success.main }}
                >
                  RANK UP
                </Typography>
              )}

              {showTierDown && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                  Dipped to {result.tierAfter.label} this round — ratings settle over many quizzes.
                </Typography>
              )}

              {result.isProvisional && (
                <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                  Your rank is still settling — play a few more quizzes and it will find its level
                  quickly.
                </Alert>
              )}
            </Box>
          )}
        </>
      ) : (
        result.unratedReason && (
          <Alert severity="info" sx={{ mb: 1, borderRadius: 2 }}>
            {unratedMessage(result.unratedReason, result.fieldSize)}
          </Alert>
        )
      )}

      {showXp && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 4 }}>
          <Typography
            sx={{
              fontFamily: 'monospace',
              fontVariantNumeric: 'tabular-nums',
              fontWeight: 700,
              color: theme.palette.secondary.dark
            }}
          >
            +{Math.round(xpValue)} XP
          </Typography>
          <StreakFlame streak={result.streak} />
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
        <Button component={RouterLink} to="/student/ranked" sx={{ textTransform: 'none' }}>
          View ranked profile
        </Button>
        <Button component={RouterLink} to="/student/quizzes" sx={{ textTransform: 'none' }}>
          Back to quizzes
        </Button>
      </Box>
    </Paper>
  );
};

export default RatingRevealCard;
